import { useState, useEffect, createContext, useContext, ReactNode, useCallback, useRef } from 'react';
import {
    User,
    onAuthStateChanged,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signInWithPopup,
    signInWithCredential,
    GoogleAuthProvider,
    signOut as firebaseSignOut,
    OAuthCredential
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { auth, db, firebaseConfigDebug } from '../lib/firebase';
import { logoutPurchases } from '../lib/purchases';
import type { UserData, Entitlement } from '../types';

// Timeouts
const GOOGLE_AUTH_TIMEOUT_MS = 25000;
const FIREBASE_SIGNIN_TIMEOUT_MS = 15000;
const NETWORK_CHECK_TIMEOUT_MS = 5000;

interface AuthContextType {
    user: User | null;
    userData: UserData | null;
    entitlement: Entitlement | null;
    loading: boolean;
    isPro: boolean;
    scansRemaining: number;
    googleAuthPending: boolean;
    authError: string | null;
    signIn: (email: string, password: string) => Promise<void>;
    signUp: (email: string, password: string) => Promise<void>;
    signInWithGoogle: () => Promise<void>;
    signOut: () => Promise<void>;
    refreshUserData: () => Promise<void>;
    clearAuthError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const FREE_SCAN_LIMIT = 10;

// Helper to safely log token info without exposing the full token
function safeTokenLog(label: string, token: unknown): void {
    if (token === null || token === undefined) {
        console.log(`[Auth] ${label}: null/undefined`);
        return;
    }

    const tokenType = typeof token;
    if (tokenType === 'string') {
        const tokenStr = token as string;
        console.log(`[Auth] ${label}: type=string, len=${tokenStr.length}, prefix=${tokenStr.substring(0, 6)}...`);
    } else if (tokenType === 'object') {
        console.log(`[Auth] ${label}: type=object, keys=${Object.keys(token as object).join(',')}`);
    } else {
        console.log(`[Auth] ${label}: type=${tokenType}`);
    }
}

// Network connectivity check
async function checkNetworkConnectivity(): Promise<boolean> {
    console.log('[Auth] Checking network connectivity...');

    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), NETWORK_CHECK_TIMEOUT_MS);

        const response = await fetch('https://www.google.com/generate_204', {
            method: 'HEAD',
            signal: controller.signal,
            cache: 'no-store'
        });

        clearTimeout(timeoutId);
        console.log('[Auth] Network check: status=', response.status, 'ok=', response.ok);
        return response.ok || response.status === 204;
    } catch (error) {
        console.error('[Auth] Network check failed:', error);
        return false;
    }
}

// Timeout wrapper for promises
function withTimeout<T>(promise: Promise<T>, timeoutMs: number, errorMessage: string): Promise<T> {
    return Promise.race([
        promise,
        new Promise<T>((_, reject) => {
            setTimeout(() => {
                reject(new Error(errorMessage));
            }, timeoutMs);
        })
    ]);
}

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [userData, setUserData] = useState<UserData | null>(null);
    const [entitlement, setEntitlement] = useState<Entitlement | null>(null);
    const [loading, setLoading] = useState(true);
    const [googleAuthPending, setGoogleAuthPending] = useState(false);
    const [authError, setAuthError] = useState<string | null>(null);
    const authTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const isPro = entitlement?.active ?? false;
    const scansRemaining = isPro ? Infinity : Math.max(0, FREE_SCAN_LIMIT - (userData?.scansUsedThisMonth ?? 0));

    const clearAuthError = useCallback(() => {
        setAuthError(null);
    }, []);

    const clearAuthTimeout = useCallback(() => {
        if (authTimeoutRef.current) {
            clearTimeout(authTimeoutRef.current);
            authTimeoutRef.current = null;
        }
    }, []);

    const fetchUserData = useCallback(async (uid: string) => {
        try {
            console.log('[Auth] Fetching user data for:', uid);
            const userDoc = await getDoc(doc(db, 'users', uid));
            if (userDoc.exists()) {
                setUserData(userDoc.data() as UserData);
            } else {
                console.log('[Auth] Creating new user document');
                const newUserData: UserData = {
                    plan: 'free',
                    scansUsedThisMonth: 0,
                    resetAt: new Date().toISOString(),
                    createdAt: new Date().toISOString()
                };
                await setDoc(doc(db, 'users', uid), {
                    ...newUserData,
                    createdAt: serverTimestamp()
                });
                setUserData(newUserData);
            }

            const entitlementDoc = await getDoc(doc(db, 'entitlements', uid));
            if (entitlementDoc.exists()) {
                setEntitlement(entitlementDoc.data() as Entitlement);
            } else {
                setEntitlement(null);
            }
        } catch (error) {
            console.error('[Auth] Error fetching user data:', error);
        }
    }, []);

    // Initialize SocialLogin on native platforms
    useEffect(() => {
        if (Capacitor.isNativePlatform()) {
            console.log('[Auth] Initializing SocialLogin for native platform');
            SocialLogin.initialize({
                google: {
                    iOSClientId: '1036772679852-07a7justaik6mvquu0rm1f5cfkoepkfv.apps.googleusercontent.com',
                    iOSServerClientId: '1036772679852-07a7justaik6mvquu0rm1f5cfkoepkfv.apps.googleusercontent.com',
                }
            }).then(() => {
                console.log('[Auth] SocialLogin initialized successfully');
            }).catch((error) => {
                console.error('[Auth] SocialLogin initialization error:', error);
            });
        }
    }, []);

    // Auth state listener
    useEffect(() => {
        console.log('[Auth] Setting up onAuthStateChanged listener');
        console.log('[Auth] Firebase config check:', firebaseConfigDebug);

        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            console.log('[Auth] Auth state changed:', user ? user.email : 'null');

            clearAuthTimeout();
            setUser(user);
            setGoogleAuthPending(false);

            if (user) {
                await fetchUserData(user.uid);
            } else {
                setUserData(null);
                setEntitlement(null);
            }
            setLoading(false);
        });

        return () => {
            unsubscribe();
            clearAuthTimeout();
        };
    }, [fetchUserData, clearAuthTimeout]);

    async function signIn(email: string, password: string) {
        console.log('[Auth] Signing in with email:', email);
        await signInWithEmailAndPassword(auth, email, password);
    }

    async function signUp(email: string, password: string) {
        console.log('[Auth] Signing up with email:', email);
        await createUserWithEmailAndPassword(auth, email, password);
    }

    async function signInWithGoogle() {
        const platform = Capacitor.getPlatform();
        console.log('[Auth] ========== GOOGLE SIGN-IN START ==========');
        console.log('[Auth] Platform:', platform);
        console.log('[Auth] Firebase config:', firebaseConfigDebug);

        setGoogleAuthPending(true);
        setAuthError(null);

        // Set up overall timeout
        clearAuthTimeout();
        authTimeoutRef.current = setTimeout(() => {
            console.error('[Auth] Google sign-in timeout after', GOOGLE_AUTH_TIMEOUT_MS, 'ms');
            setGoogleAuthPending(false);
            setAuthError('Sign-in timed out. Please check your connection and try again.');
        }, GOOGLE_AUTH_TIMEOUT_MS);

        try {
            if (Capacitor.isNativePlatform()) {
                // NATIVE FLOW
                console.log('[Auth] Using native Google Sign-In');

                // Step 1: Native sign-in
                console.log('[Auth] Step 1: Calling SocialLogin.login()...');
                const result = await SocialLogin.login({
                    provider: 'google',
                    options: {
                        scopes: ['email', 'profile'],
                    }
                });

                console.log('[Auth] Native sign-in returned, provider:', result.provider);

                if (result.provider === 'google' && result.result) {
                    const googleResult = result.result;
                    console.log('[Auth] Response type:', googleResult.responseType);

                    if (googleResult.responseType === 'online') {
                        // Log token info safely
                        safeTokenLog('idToken', googleResult.idToken);
                        safeTokenLog('accessToken', googleResult.accessToken);

                        const idToken = googleResult.idToken;
                        const accessToken = googleResult.accessToken?.token;

                        // Validate idToken is a string
                        if (!idToken) {
                            throw new Error('No ID token received from Google Sign-In');
                        }

                        if (typeof idToken !== 'string') {
                            console.error('[Auth] CRITICAL: idToken is not a string!', typeof idToken);
                            throw new Error(`Invalid idToken type: ${typeof idToken}`);
                        }

                        // Step 2: Network check
                        console.log('[Auth] Step 2: Checking network connectivity...');
                        const networkOk = await checkNetworkConnectivity();
                        if (!networkOk) {
                            console.warn('[Auth] Network check failed, proceeding anyway...');
                        }

                        // Step 3: Create credential
                        console.log('[Auth] Step 3: Creating Firebase credential...');
                        const debugRecord = {
                            platform,
                            idToken_type: typeof idToken,
                            idToken_len: idToken.length,
                            idToken_prefix: idToken.substring(0, 6),
                            accessToken_type: typeof accessToken,
                            accessToken_len: accessToken?.length || 0,
                            firebase_apiKey_present: !!firebaseConfigDebug.apiKey_len,
                            firebase_apiKey_len: firebaseConfigDebug.apiKey_len,
                            firebase_projectId: firebaseConfigDebug.projectId
                        };
                        console.log('[Auth] DEBUG RECORD:', JSON.stringify(debugRecord));

                        const credential: OAuthCredential = GoogleAuthProvider.credential(
                            idToken,
                            accessToken || null
                        );
                        console.log('[Auth] Credential created successfully');

                        // Step 4: Sign in to Firebase with timeout
                        console.log('[Auth] Step 4: Calling signInWithCredential (timeout:', FIREBASE_SIGNIN_TIMEOUT_MS, 'ms)...');
                        const startTime = Date.now();

                        try {
                            const firebaseResult = await withTimeout(
                                signInWithCredential(auth, credential),
                                FIREBASE_SIGNIN_TIMEOUT_MS,
                                `Firebase signInWithCredential timed out after ${FIREBASE_SIGNIN_TIMEOUT_MS}ms`
                            );

                            const elapsed = Date.now() - startTime;
                            console.log('[Auth] Firebase sign-in SUCCESS in', elapsed, 'ms');
                            console.log('[Auth] User email:', firebaseResult.user.email);
                            console.log('[Auth] User UID:', firebaseResult.user.uid);

                        } catch (firebaseError: unknown) {
                            const elapsed = Date.now() - startTime;
                            console.error('[Auth] Firebase sign-in FAILED after', elapsed, 'ms');

                            const err = firebaseError as {
                                name?: string;
                                code?: string;
                                message?: string;
                                stack?: string;
                            };
                            console.error('[Auth] Error details:', {
                                name: err.name,
                                code: err.code,
                                message: err.message,
                                stack: err.stack?.substring(0, 500)
                            });

                            throw firebaseError;
                        }

                    } else {
                        console.error('[Auth] Got offline response with serverAuthCode');
                        throw new Error('Google Sign-In returned offline response. Please try again.');
                    }
                } else {
                    throw new Error('Invalid response from Google Sign-In');
                }
            } else {
                // WEB FLOW
                console.log('[Auth] Using web popup flow');
                const provider = new GoogleAuthProvider();
                provider.addScope('email');
                provider.addScope('profile');

                const result = await signInWithPopup(auth, provider);
                console.log('[Auth] Web popup sign-in successful:', result.user.email);
            }

            clearAuthTimeout();
            console.log('[Auth] ========== GOOGLE SIGN-IN COMPLETE ==========');

        } catch (error: unknown) {
            clearAuthTimeout();
            setGoogleAuthPending(false);

            const err = error as { name?: string; code?: string; message?: string; stack?: string };
            console.error('[Auth] ========== GOOGLE SIGN-IN ERROR ==========');
            console.error('[Auth] Error name:', err.name);
            console.error('[Auth] Error code:', err.code);
            console.error('[Auth] Error message:', err.message);

            // Handle user cancellation gracefully
            if (err.message?.toLowerCase().includes('cancel') ||
                err.code === 'auth/popup-closed-by-user' ||
                err.code === 'auth/cancelled-popup-request') {
                console.log('[Auth] User cancelled sign-in');
                return;
            }

            // Set user-friendly error
            let userError = 'Google sign-in failed. Please try again.';
            if (err.message?.includes('timed out')) {
                userError = 'Sign-in timed out. Please check your internet connection.';
            } else if (err.code === 'auth/network-request-failed') {
                userError = 'Network error. Please check your connection.';
            } else if (err.code?.startsWith('auth/')) {
                userError = `Authentication error: ${err.code}`;
            }

            setAuthError(userError);
            throw error;
        }
    }

    async function signOut() {
        console.log('[Auth] Signing out');
        try {
            if (Capacitor.isNativePlatform()) {
                try {
                    await SocialLogin.logout({ provider: 'google' });
                    console.log('[Auth] Logged out from native Google');
                } catch (e) {
                    console.log('[Auth] Native Google logout error:', e);
                }
            }

            await logoutPurchases();
        } catch (e) {
            console.error('[Auth] Error logging out purchases:', e);
        }
        await firebaseSignOut(auth);
        setUserData(null);
        setEntitlement(null);
        console.log('[Auth] Sign out complete');
    }

    async function refreshUserData() {
        if (user) {
            await fetchUserData(user.uid);
        }
    }

    return (
        <AuthContext.Provider value={{
            user,
            userData,
            entitlement,
            loading,
            isPro,
            scansRemaining,
            googleAuthPending,
            authError,
            signIn,
            signUp,
            signInWithGoogle,
            signOut,
            refreshUserData,
            clearAuthError
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
